import type { ReactNode } from "react";
import { highlightTree, tagHighlighter, tags as t } from "@lezer/highlight";
import { typescriptLanguage } from "@codemirror/lang-javascript";

// Static syntax highlighting for code in a problem statement.
//
// Uses the same parser the editor uses, so a snippet in the statement is
// tokenised exactly the way the same characters are a few inches to the right.
// Hand-rolling a regex highlighter would have been quicker and would have
// disagreed with the editor the first time a template literal or a type
// annotation showed up.
//
// Colours are not defined here. These classes resolve to the `--tok-*` custom
// properties in styles.css, which the editor theme also reads, so there is one
// palette rather than two that drift.
const highlighter = tagHighlighter([
  { tag: t.keyword, class: "tok-keyword" },
  { tag: [t.string, t.special(t.string)], class: "tok-string" },
  { tag: [t.number, t.bool, t.null], class: "tok-number" },
  { tag: [t.function(t.variableName), t.definition(t.variableName)], class: "tok-fn" },
  { tag: [t.typeName, t.className], class: "tok-type" },
  { tag: t.comment, class: "tok-comment" },
  { tag: t.operator, class: "tok-operator" },
  { tag: t.propertyName, class: "tok-property" },
]);

/** Fence info strings that should be parsed as code rather than left as text. */
const CODE_LANGUAGES = new Set(["ts", "tsx", "typescript", "js", "jsx", "javascript"]);

export const isHighlightable = (language: string): boolean =>
  CODE_LANGUAGES.has(language.trim().toLowerCase());

/**
 * Tokenise `code` into spans.
 *
 * Only called for fences that declared a JavaScript-family language. A fence with
 * no language, such as the plain dependency listing in this drill's statement, is
 * left alone: running a TypeScript parser over prose produces confident, wrong
 * colours, which is worse than none.
 */
export function highlightCode(code: string): ReactNode[] {
  const tree = typescriptLanguage.parser.parse(code);
  const parts: ReactNode[] = [];
  let pos = 0;
  let key = 0;

  highlightTree(tree, highlighter, (from, to, classes) => {
    // Whatever the highlighter skipped (whitespace, punctuation) is plain text.
    if (from > pos) parts.push(code.slice(pos, from));
    parts.push(
      <span key={key++} className={classes}>
        {code.slice(from, to)}
      </span>,
    );
    pos = to;
  });

  if (pos < code.length) parts.push(code.slice(pos));
  return parts;
}
